import { DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Row,
  Space,
  Table,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { IntlShape } from "react-intl";

import { RewardType } from "@/api/modules/daily-login-reward";
import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import {
  FormCheckbox,
  FormInput,
  FormInputNumber,
  FormItem,
  FormSelect,
  FormSwitch,
} from "@/ui/_common_/form-helpers";
import { FormSpinner } from "@/ui/_common_/form-spinner";

import { DailyLoginRewardsFormSchema } from "./form.schema";
import { DailyLoginRewardsFormState } from "./form.state";

type Props = { state: DailyLoginRewardsFormState };
const { Text } = Typography;
export const DailyLoginRewardsFormView = observer<Props>(({ state }) => {
  if (state.query.isIdle || state.query.isPending) {
    return <FormSpinner />;
  }

  if (state.query.isRejected) {
    return <ErrorsFormatter queries={[state.query]} />;
  }

  return (
    <Space direction="vertical">
      <ErrorsFormatter queries={[state.mutation]} />
      <Form
        labelCol={{ span: 8 }}
        labelAlign="left"
        wrapperCol={{ span: 16 }}
        layout="horizontal"
        onFinish={state.handleSubmit}
      >
        <FormItem
          form={state.form}
          path="isActive"
          label={state.intl.formatMessage({ defaultMessage: "Enabled" })}
        >
          <FormSwitch form={state.form} path="isActive" />
        </FormItem>
        <FormItem
          form={state.form}
          path="cooldown"
          label={state.intl.formatMessage({
            defaultMessage: "Cooldown (hours)",
          })}
        >
          <FormInput
            form={state.form}
            path="cooldown"
            placeholder={state.intl.formatMessage({
              defaultMessage: "Enter cooldown",
            })}
          />
        </FormItem>
        <Tabs
          className="daily-login-rewards-tabs"
          items={[
            {
              key: "0",
              label: state.intl.formatMessage({ defaultMessage: "None" }),
              children: <DailySettings state={state} category={"0"} />,
            },
            {
              key: "1",
              label: "1",
              children: <DailySettings state={state} category={"1"} />,
            },
            {
              key: "2",
              label: "2",
              children: <DailySettings state={state} category={"2"} />,
            },
            {
              key: "3",
              label: "3",
              children: <DailySettings state={state} category={"3"} />,
            },
            {
              key: "4",
              label: "4",
              children: <DailySettings state={state} category={"4"} />,
            },
            {
              key: "5",
              label: "5",
              children: <DailySettings state={state} category={"5"} />,
            },
          ]}
        />
        <Row justify="end">
          <Button
            type="primary"
            htmlType="submit"
            loading={state.mutation.isPending}
          >
            {state.intl.formatMessage({ defaultMessage: "Save" })}
          </Button>
        </Row>
      </Form>
    </Space>
  );
});

type DailySettingsProps = {
  state: DailyLoginRewardsFormState;
  category: "0" | "1" | "2" | "3" | "4" | "5";
};

const optionsType = Object.values(RewardType).map((value) => {
  const label = value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return { value, label };
});

const DailySettings = observer<DailySettingsProps>(({ state, category }) => {
  const sequenceSettings =
    state.form.watch(`sequenceSettings.${category}`) ?? [];

  function addSequence() {
    const sequence = Math.min(
      Math.max(0, ...sequenceSettings.map((s) => s.sequence)) + 1,
      10,
    );
    state.form.setValue(`sequenceSettings.${category}`, [
      ...sequenceSettings,
      {
        isActive: false,
        sequence,
        reward: { amount: 0 },
        rewardType: RewardType.Credit,
      },
    ]);
  }

  function removeSequence(index: number) {
    state.form.setValue(
      `sequenceSettings.${category}`,
      sequenceSettings.filter((_, i) => i !== index),
    );
  }

  return (
    <Space direction="vertical">
      <Table
        key={sequenceSettings.length}
        size="small"
        rowKey={(_, index) => category + "." + index}
        dataSource={sequenceSettings}
        columns={[
          {
            title: state.intl.formatMessage({ defaultMessage: "Enabled" }),
            dataIndex: "isActive",
            onCell: () => ({ style: { verticalAlign: "top" } }),
            key: "isActive",
            render: (_0, _1, index) => (
              <FormCheckbox
                form={state.form}
                path={`sequenceSettings.${category}.${index}.isActive`}
              />
            ),
          },
          {
            title: state.intl.formatMessage({ defaultMessage: "Sequence" }),
            dataIndex: "sequence",
            key: "sequence",
            width: "100px",
            onCell: () => ({ style: { verticalAlign: "top" } }),
            render: (_0, _1, index) => {
              return (
                <SequenceCell
                  form={state.form}
                  category={category}
                  index={index}
                  intl={state.intl}
                />
              );
            },
          },
          {
            title: state.intl.formatMessage({ defaultMessage: "Type" }),
            dataIndex: "rewardType",
            key: "rewardType",
            onCell: () => ({ style: { verticalAlign: "top" } }),
            render: (_0, _1, index) => (
              <FormSelect<DailyLoginRewardsFormSchema, RewardType>
                form={state.form}
                path={`sequenceSettings.${category}.${index}.rewardType`}
                options={optionsType.map((option) => ({
                  ...option,
                  label: option.label,
                }))}
                size="small"
                style={{ width: "100%" }}
              />
            ),
          },
          {
            title: state.intl.formatMessage({ defaultMessage: "Parameters" }),
            dataIndex: "reward",
            key: "reward",
            onCell: () => ({ style: { verticalAlign: "top" } }),
            render: (_0, _1, index) => {
              return (
                <RewardCell
                  form={state.form}
                  category={category}
                  index={index}
                  intl={state.intl}
                />
              );
            },
          },
          {
            title: state.intl.formatMessage({ defaultMessage: "Actions" }),
            key: "actions",
            align: "right",
            onCell: () => ({ style: { verticalAlign: "top" } }),
            render: (_0, _1, index) => (
              <Row justify="end">
                {sequenceSettings.length > 1 ? (
                  <Button
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeSequence(index)}
                  />
                ) : (
                  "—"
                )}
              </Row>
            ),
          },
        ]}
        pagination={false}
      />
      <Row justify="end">
        <Button
          size="small"
          onClick={addSequence}
          disabled={sequenceSettings.length >= 10}
        >
          {state.intl.formatMessage({ defaultMessage: "Add" })}
        </Button>
      </Row>
    </Space>
  );
});

type SequenceCellProps = {
  form: UseFormReturn<DailyLoginRewardsFormSchema>;
  category: "0" | "1" | "2" | "3" | "4" | "5";
  index: number;
  intl: IntlShape;
};

function SequenceCell({ form, category, index }: SequenceCellProps) {
  const error =
    form.formState.errors.sequenceSettings?.[category]?.[index]?.sequence
      ?.message ?? null;

  return (
    <Tooltip
      open={!!error}
      overlay={error}
      color="red"
      getTooltipContainer={(node) => node.closest(".ant-tabs-content-holder")!}
    >
      <FormInput
        form={form}
        path={`sequenceSettings.${category}.${index}.sequence`}
        size="small"
      />
    </Tooltip>
  );
}

export function RewardCell({ form, category, index, intl }: SequenceCellProps) {
  const rewardType = form.getValues(
    `sequenceSettings.${category}.${index}.rewardType`,
  );
  const prevRewardType = useRef(rewardType);

  useEffect(() => {
    if (prevRewardType.current !== rewardType) {
      form.resetField(`sequenceSettings.${category}.${index}.reward`);
      if (rewardType === RewardType.Credit) {
        form.setValue(`sequenceSettings.${category}.${index}.reward`, {
          amount: 0,
        });
      } else if (rewardType === RewardType.FortuneWheel) {
        form.setValue(`sequenceSettings.${category}.${index}.reward`, {
          spins: 0,
        });
      } else if (rewardType === RewardType.BonusBoost) {
        form.setValue(`sequenceSettings.${category}.${index}.reward`, {
          bonusBoost: 0,
          depositCount: 0,
          maxDepositPeriod: null,
        });
      } else {
        form.setValue(`sequenceSettings.${category}.${index}.reward`, {
          amount: 0,
        });
      }
      prevRewardType.current = rewardType;
    }
  }, [rewardType, form, category, index]);

  const renderCreditField = () => {
    const rewardError =
      form.formState.errors.sequenceSettings?.[category]?.[index]?.reward;
    const errorAmount = (rewardError as { amount?: { message?: string } })
      ?.amount?.message;

    return (
      <Row align="middle" gutter={8}>
        <Col>
          <Text>{intl.formatMessage({ defaultMessage: "Amount" })}</Text>
        </Col>
        <Col flex="auto">
          <Tooltip
            open={!!errorAmount}
            overlay={errorAmount}
            color="red"
            getTooltipContainer={(node) =>
              node?.closest(".ant-tabs-content-holder") || document.body
            }
          >
            <FormInputNumber
              form={form}
              path={`sequenceSettings.${category}.${index}.reward.amount`}
              size="small"
              placeholder={intl.formatMessage({ defaultMessage: "Amount" })}
              style={{ width: "100%" }}
            />
          </Tooltip>
        </Col>
      </Row>
    );
  };

  const renderFortuneWheelField = () => {
    const rewardError =
      form.formState.errors.sequenceSettings?.[category]?.[index]?.reward;
    const errorSpins = (rewardError as { spins?: { message?: string } })?.spins
      ?.message;

    return (
      <Row align="middle" gutter={8}>
        <Col>
          <Text>{intl.formatMessage({ defaultMessage: "Spins" })}</Text>
        </Col>
        <Col flex="auto">
          <Tooltip
            open={!!errorSpins}
            overlay={errorSpins}
            color="red"
            getTooltipContainer={(node) =>
              node?.closest(".ant-tabs-content-holder") || document.body
            }
          >
            <FormInputNumber
              form={form}
              path={`sequenceSettings.${category}.${index}.reward.spins`}
              size="small"
              placeholder={intl.formatMessage({ defaultMessage: "Spins" })}
              style={{ width: "100%" }}
            />
          </Tooltip>
        </Col>
      </Row>
    );
  };

  const renderBonusBoostField = () => {
    const rewardError =
      form.formState.errors.sequenceSettings?.[category]?.[index]?.reward;
    const errorBonusBoost = (
      rewardError as { bonusBoost?: { message?: string } }
    )?.bonusBoost?.message;
    const errorDepositCount = (
      rewardError as { depositCount?: { message?: string } }
    )?.depositCount?.message;
    const errorMaxDepositPeriod = (
      rewardError as { maxDepositPeriod?: { message?: string } }
    )?.maxDepositPeriod?.message;

    const errorMessages: string[] = [];
    if (errorBonusBoost) {
      errorMessages.push(
        `${intl.formatMessage({
          defaultMessage: "Bonus Boost",
        })}: ${errorBonusBoost}`,
      );
    }
    if (errorDepositCount) {
      errorMessages.push(
        `${intl.formatMessage({
          defaultMessage: "Deposit Count",
        })}: ${errorDepositCount}`,
      );
    }
    if (errorMaxDepositPeriod) {
      errorMessages.push(
        `${intl.formatMessage({
          defaultMessage: "Max Deposit Period",
        })}: ${errorMaxDepositPeriod}`,
      );
    }

    const hasError = errorMessages.length > 0;
    const tooltipContent = errorMessages.join("\n");

    return (
      <Tooltip
        open={hasError}
        title={tooltipContent}
        color="red"
        getPopupContainer={(node) =>
          node?.closest(".ant-tabs-content-holder") || document.body
        }
      >
        <div>
          <Row align="middle" gutter={8} style={{ marginBottom: 8 }}>
            <Col>
              <Text>
                {intl.formatMessage({ defaultMessage: "Bonus Boost" })}
              </Text>
            </Col>
            <Col flex="auto">
              <FormInputNumber
                form={form}
                path={`sequenceSettings.${category}.${index}.reward.bonusBoost`}
                size="small"
                placeholder={intl.formatMessage({
                  defaultMessage: "Bonus Boost",
                })}
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
          <Row align="middle" gutter={8} style={{ marginBottom: 8 }}>
            <Col>
              <Text>
                {intl.formatMessage({ defaultMessage: "Deposit Count" })}
              </Text>
            </Col>
            <Col flex="auto">
              <FormInputNumber
                form={form}
                path={`sequenceSettings.${category}.${index}.reward.depositCount`}
                size="small"
                placeholder={intl.formatMessage({
                  defaultMessage: "Deposit Count",
                })}
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
          <Row align="middle" gutter={8}>
            <Col>
              <Text>
                {intl.formatMessage({ defaultMessage: "Max Deposit Period" })}
              </Text>
            </Col>
            <Col flex="auto">
              <FormInputNumber
                form={form}
                path={`sequenceSettings.${category}.${index}.reward.maxDepositPeriod`}
                size="small"
                placeholder={intl.formatMessage({
                  defaultMessage: "Max Deposit Period",
                })}
                style={{ width: "100%", minWidth: "150px" }}
              />
            </Col>
          </Row>
        </div>
      </Tooltip>
    );
  };

  const renderField = () => {
    switch (rewardType) {
      case RewardType.Credit:
        return renderCreditField();
      case RewardType.FortuneWheel:
        return renderFortuneWheelField();
      case RewardType.BonusBoost:
        return renderBonusBoostField();
      default:
        return null;
    }
  };

  return <>{renderField()}</>;
}
